/**
 * EVERY FAILURE BECOMES ONE TYPED AppError.
 *
 * THE SERVER WORDS ITS OWN REFUSALS (Rachel, 21-09-2026, narrowed 24-09-2026).
 * The screen prints `message` and nothing else. A status does not pick a
 * sentence or a named state: the frontend spec's catalogue (§5.2) is not
 * how a failure is drawn. `code` stays for the rare screen that must BEHAVE
 * differently on one refusal; it never picks words.
 *
 * The acts a status still triggers, and only these:
 *   401 unauthenticated   an active session ends; a sign-in refusal stays
 *   429 rate-limited      a query may retry
 *   5xx server            a query may retry. The trace id is kept, not shown
 *   no response           network, or timeout (a timed-out branch exchange
 *                         is not sent again)
 *   contract              this build could not read the body
 *   anything else         failure: display `message`, and do nothing else
 */
export type AppErrorKind =
  "unauthenticated" | "rate-limited" | "server" | "network" | "timeout" | "contract" | "failure";

export interface Problem {
  type?: string;
  title?: string;
  status?: number;
  detail?: string;
  instance?: string;
  traceId?: string;
  /** Stable machine code, e.g. "branch.last_open". */
  code?: string;
  params?: Record<string, string | number>;
  /** Field → message(s), worded by the server. */
  errors?: Record<string, string | string[]>;
  /** The refusal, worded by the server. Shown as sent. */
  message?: string;
}

export class AppError extends Error {
  readonly kind: AppErrorKind;
  readonly status: number | undefined;
  readonly code: string | undefined;
  readonly params: Record<string, string | number>;
  readonly fieldErrors: Record<string, string[]>;
  readonly traceId: string | undefined;
  /** The server's sentence, shown as sent; undefined when it sent none. */
  readonly serverMessage: string | undefined;
  readonly retryAfterSeconds: number | undefined;
  readonly problem: Problem | undefined;

  constructor(
    kind: AppErrorKind,
    init: {
      status?: number;
      problem?: Problem;
      retryAfterSeconds?: number;
      cause?: unknown;
      message?: string;
    } = {},
  ) {
    const problem = cleanProblem(init.problem);
    super(init.message ?? problem?.message ?? problem?.detail ?? problem?.title ?? kind, {
      cause: init.cause,
    });
    this.name = "AppError";
    this.kind = kind;
    this.status = init.status;
    this.problem = problem;
    this.code = problem?.code;
    this.params = problem?.params ?? {};
    this.traceId = problem?.traceId;
    this.serverMessage = problem?.message?.trim() || undefined;
    this.retryAfterSeconds = init.retryAfterSeconds;
    this.fieldErrors = normaliseFieldErrors(problem?.errors);
  }
}

export function isAppError(value: unknown): value is AppError {
  return value instanceof AppError;
}

/** The few statuses the client acts on. Every other answered status is `failure`. */
export function kindForStatus(status: number): AppErrorKind {
  if (status === 401) return "unauthenticated";
  if (status === 429) return "rate-limited";
  if (status >= 500) return "server";
  return "failure";
}

/**
 * Reads a failed response into an AppError. Never throws while doing so.
 * A body that is not the contract's object — a number where a sentence belongs,
 * a proxy's HTML, an empty body — keeps the status and drops the detail.
 */
export async function errorFromResponse(response: Response): Promise<AppError> {
  let problem: Problem | undefined;
  const type = response.headers.get("content-type") ?? "";
  if (type.includes("json")) {
    try {
      problem = cleanProblem(await response.json());
    } catch {
      // A body that says JSON and is not: keep the status, lose the detail.
    }
  }
  const retry = Number(response.headers.get("retry-after"));
  return new AppError(kindForStatus(response.status), {
    status: response.status,
    ...(problem ? { problem } : {}),
    ...(Number.isFinite(retry) && retry > 0 ? { retryAfterSeconds: retry } : {}),
  });
}

/** A string the contract promised, or nothing. A number or an object is not a sentence. */
function text(value: unknown): string | undefined {
  return typeof value === "string" && value !== "" ? value : undefined;
}

/**
 * The fields this app reads, and only those, copied off a body that may not be
 * the object the contract describes. Anything else is dropped rather than thrown.
 */
function cleanProblem(body: unknown): Problem | undefined {
  if (!body || typeof body !== "object" || Array.isArray(body)) return undefined;
  const record = body as Record<string, unknown>;
  const problem: Problem = {};
  const type = text(record.type);
  const title = text(record.title);
  const detail = text(record.detail);
  const instance = text(record.instance);
  const traceId = text(record.traceId);
  const code = text(record.code);
  const message = text(record.message);
  if (type) problem.type = type;
  if (title) problem.title = title;
  if (typeof record.status === "number") problem.status = record.status;
  if (detail) problem.detail = detail;
  if (instance) problem.instance = instance;
  if (traceId) problem.traceId = traceId;
  if (code) problem.code = code;
  if (message) problem.message = message;
  const params = paramsOf(record.params);
  if (params) problem.params = params;
  if (record.errors && typeof record.errors === "object" && !Array.isArray(record.errors)) {
    problem.errors = record.errors as Problem["errors"];
  }
  return Object.keys(problem).length > 0 ? problem : undefined;
}

function paramsOf(value: unknown): Record<string, string | number> | undefined {
  if (!value || typeof value !== "object" || Array.isArray(value)) return undefined;
  const params: Record<string, string | number> = {};
  for (const [key, item] of Object.entries(value)) {
    if (typeof item === "string" || typeof item === "number") params[key] = item;
  }
  return Object.keys(params).length > 0 ? params : undefined;
}

function normaliseFieldErrors(errors: Problem["errors"]): Record<string, string[]> {
  if (!errors || typeof errors !== "object") return {};
  const out: Record<string, string[]> = {};
  for (const [field, value] of Object.entries(errors)) {
    const list = Array.isArray(value) ? value : [value];
    const messages = list.filter((m): m is string => typeof m === "string" && m !== "");
    if (messages.length) out[field] = messages;
  }
  return out;
}

/** Retrying helps only where the server did not answer, or answered that it is overloaded. */
export function isRetryable(error: unknown): boolean {
  if (!isAppError(error)) return false;
  return (
    error.kind === "network" ||
    error.kind === "timeout" ||
    error.kind === "server" ||
    error.kind === "rate-limited"
  );
}
